// Raw API client for docs' console.
//
// The console deliberately bypasses the shared baseAxios instance: humps
// camelize/decamelize would silently rewrite user-authored JSON keys — group
// names in the group grants, policy keys, webhook payloads. Console code
// therefore talks snake_case end-to-end, exactly what docs-api serves.
//
// docs-api is on this page's own origin, where its BFF session cookie goes;
// @zehnmindai/embed's interceptor sends the cookie and the X-Zehn-CSRF token,
// retries once after a refused CSRF token or a session renewed elsewhere, and
// reports a session that stays lost. No token is held or sent here.

import axios from 'axios';
import { installAxiosInterceptor } from '@zehnmindai/embed/bff';
import { apiErrorMessage, parseApiError } from '@/shared/utils/apiError.js';

const raw = axios.create({ baseURL: '/' });
installAxiosInterceptor(raw);

export async function consoleRequest(method, url, { data, params, headers, responseType } = {}) {
  try {
    const res = await raw({ method, url, data, params, headers, responseType });
    return { success: true, response: res.data, status: res.status };
  } catch (e) {
    // No humps on this client, so the envelope arrives snake_case — the
    // shared parser handles both; display text resolves from the code.
    const errorInfo = parseApiError(e);
    const message = apiErrorMessage(errorInfo);
    return { success: false, error: e.response, errorInfo, message, status: e.response?.status };
  }
}

export const api = {
  get: (url, opts) => consoleRequest('GET', url, opts),
  post: (url, opts) => consoleRequest('POST', url, opts),
  patch: (url, opts) => consoleRequest('PATCH', url, opts),
  put: (url, opts) => consoleRequest('PUT', url, opts),
  del: (url, opts) => consoleRequest('DELETE', url, opts),
};

// A download through the session (an export is a GET that must carry the
// cookie and come back as a file): GET as a blob, hand it to the browser.
export async function downloadFile(url, { params, filename } = {}) {
  const res = await consoleRequest('GET', url, { params, responseType: 'blob' });
  if (!res.success) {
    // With responseType 'blob' an error envelope arrives as a Blob too:
    // decode it so the toast shows the code's text, not a generic message.
    const data = res.error?.data;
    if (data instanceof Blob) {
      try {
        const parsed = JSON.parse(await data.text());
        const errorInfo = parseApiError({ response: { status: res.status, data: parsed } });
        return { ...res, errorInfo, message: apiErrorMessage(errorInfo) };
      } catch { /* not JSON — keep the generic message */ }
    }
    return res;
  }
  const href = URL.createObjectURL(res.response);
  const a = document.createElement('a');
  a.href = href;
  a.download = filename || 'download';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(href), 1000);
  return res;
}

// ── docs-api's console routes (install-wide, `docs:admin`) ─────────────────
// Every id in a path is the thing's uuid.
const SETTINGS = '/api/v1/settings';

export const ep = {
  // The install's settings: group grants, its name, upload defaults/limits.
  settings: () => SETTINGS,
  // The scope catalogue and presets (what a group grant or an API key names).
  scopes: () => '/api/v1/auth/scopes',

  apiKeys: () => '/api/v1/api-keys',
  apiKey: (keyUuid) => `/api/v1/api-keys/${keyUuid}`,

  users: () => `${SETTINGS}/users`,

  policies: () => `${SETTINGS}/policies`,
  policyArea: (area) => `${SETTINGS}/policies/${area}`,
  signing: () => `${SETTINGS}/signing`,

  indexingSummary: () => `${SETTINGS}/indexing/summary`,
  indexingFiles: () => `${SETTINGS}/indexing/files`,
  indexingRetry: () => `${SETTINGS}/indexing/retry`,
  indexingRebuild: () => `${SETTINGS}/indexing/rebuild`,
  indexingJobs: () => `${SETTINGS}/indexing/jobs`,
  indexingJobCancel: (jobUuid) => `${SETTINGS}/indexing/jobs/${jobUuid}/cancel`,
  indexingDoctor: () => `${SETTINGS}/indexing/doctor`,
  indexingCleanup: () => `${SETTINGS}/indexing/cleanup`,
  indexingHealth: () => `${SETTINGS}/indexing/health`,

  retention: () => `${SETTINGS}/retention`,
  retentionSections: () => `${SETTINGS}/retention/sections`,
  retentionSection: (sectionUuid) => `${SETTINGS}/retention/sections/${sectionUuid}`,
  retentionClasses: () => `${SETTINGS}/retention/classes`,
  retentionClass: (classUuid) => `${SETTINGS}/retention/classes/${classUuid}`,
  retentionReapply: (classUuid) => `${SETTINGS}/retention/classes/${classUuid}/reapply`,
  retentionStandard: () => `${SETTINGS}/retention/standard`,
  retentionStandardImport: () => `${SETTINGS}/retention/standard/import`,
  retentionImport: () => `${SETTINGS}/retention/import`,
  retentionExport: () => `${SETTINGS}/retention/export`,

  webhooks: () => `${SETTINGS}/webhooks`,
  webhook: (webhookUuid) => `${SETTINGS}/webhooks/${webhookUuid}`,
  webhookCatalogue: () => `${SETTINGS}/webhooks/catalogue`,
  webhookRules: () => `${SETTINGS}/webhooks/rules`,
  webhookTest: (webhookUuid) => `${SETTINGS}/webhooks/${webhookUuid}/test`,
  webhookRotate: (webhookUuid) => `${SETTINGS}/webhooks/${webhookUuid}/rotate-secret`,
  webhookDeliveries: (webhookUuid) => `${SETTINGS}/webhooks/${webhookUuid}/deliveries`,
  webhookDelivery: (webhookUuid, deliveryUuid) => `${SETTINGS}/webhooks/${webhookUuid}/deliveries/${deliveryUuid}`,
  webhookRedeliver: (webhookUuid, deliveryUuid) =>
    `${SETTINGS}/webhooks/${webhookUuid}/deliveries/${deliveryUuid}/redeliver`,

  audit: () => `${SETTINGS}/audit`,
  storage: () => `${SETTINGS}/storage`,
};
